import React from 'react';
import axios from 'axios';
import ItemDropdown from '../items/item_dropdown';
import { PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import SimpleNumberInput from '../common/simple_number_input';
import { getHeaders } from '../../lib/requests';
import GeneralButton from '../common/button_general';

const OrderItems = ({
  items,
  orderItems,
  handleItemChange,
  addItem,
  removeItem,
  maxItems,
  disabled,
  quantityRefs,
  allowOnlyLatestEditable = true,
  refreshOrderItems,
  disableAddItems = true,
  orderId,
  onError,
  onSuccess,
  clearInsteadOfDelete = false,
}) => {
  const apiToken = localStorage.getItem('accessToken');
  const apiUrl = process.env.REACT_APP_API_URL;

  const allItemsSelected = orderItems.every((item) => item.item_id);

  const handleQuantityChange = (idx, rawValue) => {
    let newValue = parseInt(rawValue, 10) || 0;
    const currentItem = items.find((i) => i.item_id === orderItems[idx].item_id);
    if (!currentItem) return;

    if (currentItem.stock_status !== 'in_stock') {
      newValue = 0;
    }

    newValue = Math.max(0, Math.min(newValue, maxItems || 99));

    if (currentItem?.type?.toLowerCase() === 'entree') {
      const maxEntreeQuantity = maxItems || 0;
      const currentTotal = orderItems.reduce((total, oi) => {
        if (oi.item_id === currentItem.item_id && oi.quantity === orderItems[idx].quantity) {
          return total;
        }
        const checkItem = items.find(ii => ii.item_id === oi.item_id);
        if (checkItem?.type?.toLowerCase() === 'entree') {
          return total + oi.quantity;
        }
        return total;
      }, 0);

      const remainingAllowed = maxEntreeQuantity - currentTotal;
      newValue = Math.min(newValue, remainingAllowed);
      newValue = Math.max(0, newValue);
    }

    handleItemChange(idx, 'quantity', newValue);
  };

  const handleRemoveItem = async (index, orderItemId) => {
    if (orderItemId) {
      try {
        await axios.delete(`${apiUrl}/order-items/${orderItemId}`, {
          headers: getHeaders(apiToken),
        });
        removeItem(index);
      } catch (error) {
        console.error('Failed to delete order item', error);
      }
    } else {
      removeItem(index);
    }
  };

  const handleClearItem = (index) => {
    handleItemChange(index, 'quantity', 0);
  };

  const handleSetZeroForAll = () => {
    orderItems.forEach((_, index) => {
      handleItemChange(index, 'quantity', 0);
    });
  };

  const handleDeleteAllItems = async () => {
    try {
      const isConfirmed = window.confirm(
        'Are you sure you want to delete all items from this order? This action cannot be undone.'
      );
    
      if (!isConfirmed) return;

      await axios.delete(`${apiUrl}/orders/${orderId}/items`, {
        headers: getHeaders(apiToken),
      });
      
      orderItems.forEach((_, index) => removeItem(index));
      refreshOrderItems();

      if (onSuccess) onSuccess('All items deleted successfully');
    } catch (error) {
      console.error('Failed to delete all items:', error);
      onError && onError('Failed to delete all items');
    }
  };

  const formatStockStatus = (status) =>
    status
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const getNextInStockIndex = (fromIndex) => {
    for (let i = fromIndex + 1; i < orderItems.length; i++) {
      const item = items.find((itm) => itm.item_id === orderItems[i].item_id);
      if (item && item.stock_status === 'in_stock') {
        return i;
      }
    }
    return null;
  };

  const getPrevInStockIndex = (fromIndex) => {
    for (let i = fromIndex - 1; i >= 0; i--) {
      const item = items.find((itm) => itm.item_id === orderItems[i].item_id);
      if (item && item.stock_status === 'in_stock') {
        return i;
      }
    }
    return null;
  };

  return (
    <div className="mb-4">
      {orderItems?.length > 0 ? (
        orderItems
          .filter(item => !disableAddItems || item.item_id)          
          .map((item, index) => {
            const selectedItem = items.find((i) => i.item_id === item.item_id);
            const stockStatus = selectedItem?.stock_status || 'in_stock';
            const isOutOfStock = stockStatus !== 'in_stock';
            
            const isDropdownDisabled = disabled || disableAddItems || (allowOnlyLatestEditable && index !== orderItems.length - 1);

            return item.item_id && (
              <div
                key={index}
                className={`grid grid-cols-8 gap-4 items-center ${isOutOfStock ? 'border-2 border-red-500 rounded-md' : ''}`}
              >
                <div className="col-span-8 md:col-span-7 lg:col-span-7">
                  <div className="flex flex-col md:flex-row md:items-center">
                    <div className="flex-1">
                      <ItemDropdown
                        label="Item"
                        items={items}
                        name="item_id"
                        value={item.item_id}
                        onSelect={(value) => handleItemChange(index, 'item_id', value)}
                        disabled={isDropdownDisabled}
                        showLabels={index === 0}
                        showAllergenInfo={true}
                        showCategory={false}
                        showSubcategory={false}
                      />
                      {isOutOfStock && (
                        <p className="text-xs text-red-500 mt-1">
                          Status: {formatStockStatus(stockStatus)}
                        </p>
                      )}
                    </div>
                    <div className="w-full md:w-auto">
                      <SimpleNumberInput
                        min={0}
                        max={isOutOfStock ? 0 : maxItems || 99}
                        value={item.quantity}
                        onChange={(value) =>
                          handleQuantityChange(index, value)
                        }
                        ref={(el) => (quantityRefs.current[index] = el)}
                        onEnterPress={() => {
                          const nextIndex = getNextInStockIndex(index);
                          if (nextIndex !== null && quantityRefs.current[nextIndex]) {
                            quantityRefs.current[nextIndex].focus();
                          }
                        }}
                        onPrevPress={() => {
                          const prevIndex = getPrevInStockIndex(index);
                          if (prevIndex !== null && quantityRefs.current[prevIndex]) {
                            quantityRefs.current[prevIndex].focus();
                          }
                        }}
                        disabled={disabled || isOutOfStock}
                        showLabels={index === 0}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-8 md:col-span-1 lg:col-span-1 flex justify-start items-center">
                  <button
                    type="button"
                    onClick={() =>
                      clearInsteadOfDelete
                        ? handleClearItem(index)
                        : handleRemoveItem(index, item.order_item_id)
                    }
                    className={`text-red-500 hover:text-white hover:bg-red-500 p-2 rounded focus:outline-none transition-colors duration-150 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={disabled}
                  >
                    {clearInsteadOfDelete ? (
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <TrashIcon className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
            );
          })
      ) : !disableAddItems && (
        <div className="text-center mb-2">
          <p className="text-gray-700">No items added. Click + to add an item.</p>
        </div>
      )}
      {!disableAddItems && allItemsSelected && !disabled && !disableAddItems && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={addItem}
            className="mt-2 text-green-500 hover:text-white hover:bg-green-500 p-2 rounded focus:outline-none transition-colors duration-150"
            disabled={disabled}
          >
            <PlusIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      )}
      {orderItems?.length > 0 && (
        <div className="flex justify-center space-x-2">
          <GeneralButton type="button" buttonType="secondary_action" onClick={handleSetZeroForAll}>
            Clear Selections
          </GeneralButton>
          {!clearInsteadOfDelete && (
            <GeneralButton type="button" buttonType="secondary_action" onClick={handleDeleteAllItems}>
              Delete All Order Items
            </GeneralButton>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderItems;
